import axios from 'axios';
import { getApiEndpoint, Nullable, User, UserWithToken } from './api.types';

// Cookie name for CSRF token (must match backend)
/**
 * Cuerpo de la respuesta de error de axios, tipado.
 *
 * Antes cada `catch` tipaba el error como `any` y encadenaba
 * `error?.response?.data?.message`: si el backend renombraba el campo, el
 * encadenamiento devolvía `undefined` y caía al fallback en silencio. Con el
 * type guard de axios, el nombre del campo lo valida el compilador contra el
 * tipo de respuesta que declara cada método.
 */
function errorBody<T>(error: unknown): Partial<T> | undefined {
  return axios.isAxiosError<T>(error) ? error.response?.data : undefined;
}

const CSRF_COOKIE_NAME = 'csrfToken';
const CSRF_STORAGE_KEY = 'ygoarg_csrf_token';

// Create axios instance for auth requests with credentials
const authClient = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable cookies
});

export interface LoginCredentials {
  email?: string;
  cossyId?: string;
  password: string;
  service?: string;
  version?: string;
}

export interface LoginResponse {
  error: boolean;
  message: string;
  detail?: string;
  data?: {
    user: UserWithToken;
    csrfToken: string;
    accessToken?: string; // Legacy token for backwards compatibility
  };
}

export interface RefreshResponse {
  error: boolean;
  message: string;
  data?: {
    csrfToken: string;
  };
}

export interface MeResponse {
  error: boolean;
  message: string;
  data?: {
    user: Omit<UserWithToken, 'accessToken'>;
    csrfToken?: string;
  };
}

/** Resultado de `verifySession`. `reason` va a los logs de quien pregunta. */
export type SessionCheck =
  | { status: 'active'; user: User }
  | { status: 'none'; reason: string }
  | { status: 'unknown'; reason: string };

export interface RegisterCredentials {
  name: string;
  lastName: string;
  email?: string;
  cossyId?: string;
  password: string;
  service?: string;
  version?: string;
}

export interface FirstLoginCredentials {
  userId: number;
  cossyId: string;
  password: string;
  service?: string;
  version?: string;
}

export interface RecoverPasswordInput {
  email?: string;
  cossyId?: string;
  source?: string;
}

export interface RecoverPasswordResponse {
  error: boolean;
  message: string;
  data?: {
    id: number;
    name: string;
    lastName: string;
    email: string;
    cossyId: string | null;
  };
}

export interface ValidateResetTokenResponse {
  error: boolean;
  message: string;
  data?: {
    id: number;
    email: string;
  };
}

export interface ResetPasswordResponse {
  error: boolean;
  message: string;
  data?: {
    id: number;
  };
}

/**
 * AuthService handles REST-based authentication with cookies
 * This replaces the old GraphQL-based auth that stored tokens in IndexedDB
 */
class AuthService {
  private csrfToken: string | null = null;

  constructor() {
    // Try to restore CSRF token from localStorage on init
    // Check both window and localStorage exist (localStorage doesn't exist in React Native)
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      this.csrfToken = localStorage.getItem(CSRF_STORAGE_KEY);
    }
  }

  /**
   * Get the auth API endpoint
   */
  private getAuthEndpoint(): string {
    return `${getApiEndpoint()}/auth`;
  }

  /**
   * Login with email/cossyId and password
   * Sets HttpOnly cookies for access and refresh tokens
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await authClient.post<LoginResponse>(
        `${this.getAuthEndpoint()}/login`,
        credentials
      );

      const data = response.data;

      // Store CSRF token
      if (data.data?.csrfToken) {
        this.setCsrfToken(data.data.csrfToken);
      }

      return data;
    } catch (error) {
      console.error('Login error:', error);
      return {
        error: true,
        message: errorBody<LoginResponse>(error)?.message || 'network_error',
      };
    }
  }

  /**
   * Logout - clears cookies and local storage
   */
  async logout(): Promise<void> {
    try {
      await authClient.post(
        `${this.getAuthEndpoint()}/logout`,
        {},
        {
          headers: {
            'X-CSRF-Token': this.getCsrfToken(),
          },
        }
      );
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage regardless of API response
      this.clearCsrfToken();
    }
  }

  /**
   * Refresh the session using the refresh token cookie
   * Returns new CSRF token if successful
   */
  async refreshSession(): Promise<boolean> {
    try {
      const response = await authClient.post<RefreshResponse>(
        `${this.getAuthEndpoint()}/refresh`
      );

      if (response.data?.data?.csrfToken) {
        this.setCsrfToken(response.data.data.csrfToken);
      }

      return true;
    } catch (error) {
      console.error('Refresh session error:', error);
      // Only clear CSRF token on explicit 401 - not on network errors
      // Network errors don't mean the session is invalid, just that the server is unreachable
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        this.clearCsrfToken();
      }
      return false;
    }
  }

  /**
   * Get current user info from session
   * Also handles automatic token refresh if needed
   */
  async getCurrentUser(): Promise<Nullable<User>> {
    const session = await this.verifySession();
    return session.status === 'active' ? session.user : null;
  }

  /**
   * Como `getCurrentUser`, pero distingue «el API confirmó que no hay sesión»
   * (`none`) de «no se pudo saber» (`unknown`: red caída, 5xx).
   *
   * Es lo que hay que preguntar antes de desloguear a alguien por una señal
   * indirecta —un error que dice `unauthorized`, otra pestaña que borró el
   * CSRF—: el API usa `unauthorized` también para negar permisos, y el CSRF
   * del localStorage se borra en casos en que la cookie sigue valiendo.
   */
  async verifySession(): Promise<SessionCheck> {
    // Skip request if no CSRF token - user is definitely not logged in
    const csrfToken = this.getCsrfToken();
    if (!csrfToken) {
      return { status: 'none', reason: 'no_csrf_token' };
    }

    try {
      const response = await authClient.get<MeResponse>(
        `${this.getAuthEndpoint()}/me`,
        {
          headers: {
            'X-CSRF-Token': csrfToken,
          },
        }
      );

      const user = response.data?.data?.user || null;

      // If user is null (server confirmed no session), clear stale CSRF token
      if (!user) {
        this.clearCsrfToken();
        return {
          status: 'none',
          reason: response.data?.message || 'no_user',
        };
      }

      // Update CSRF token if a new one was provided (after refresh)
      if (response.data?.data?.csrfToken) {
        this.setCsrfToken(response.data.data.csrfToken);
      }

      return { status: 'active', user };
    } catch (error) {
      // Only clear CSRF token on explicit 401 - not on network errors
      // Network errors (timeout, connection refused, etc.) don't mean the session is invalid
      // This prevents users from being logged out during temporary network issues or long tournament rounds
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        this.clearCsrfToken();
        return { status: 'none', reason: 'http_401' };
      }
      return {
        status: 'unknown',
        reason: error instanceof Error ? error.message : String(error),
      };
    }
  }

  /**
   * Check if user has a valid session (quick check without full user data)
   */
  async hasValidSession(): Promise<boolean> {
    const user = await this.getCurrentUser();
    return user !== null;
  }

  /**
   * Register a new user
   */
  async register(credentials: RegisterCredentials): Promise<LoginResponse> {
    try {
      const response = await authClient.post<LoginResponse>(
        `${this.getAuthEndpoint()}/register`,
        credentials
      );

      const data = response.data;

      if (data.data?.csrfToken) {
        this.setCsrfToken(data.data.csrfToken);
      }

      return data;
    } catch (error) {
      console.error('Register error:', error);
      const data = errorBody<LoginResponse>(error);
      return {
        error: true,
        message: data?.message || 'network_error',
        detail: data?.detail,
      };
    }
  }

  /**
   * First-time login (set password for migrated users)
   */
  async firstLogin(credentials: FirstLoginCredentials): Promise<LoginResponse> {
    try {
      const response = await authClient.post<LoginResponse>(
        `${this.getAuthEndpoint()}/first-login`,
        credentials
      );

      const data = response.data;

      if (data.data?.csrfToken) {
        this.setCsrfToken(data.data.csrfToken);
      }

      return data;
    } catch (error) {
      console.error('First login error:', error);
      return {
        error: true,
        message: errorBody<LoginResponse>(error)?.message || 'network_error',
      };
    }
  }

  /**
   * Request password recovery email
   */
  async recoverPassword(
    input: RecoverPasswordInput
  ): Promise<RecoverPasswordResponse> {
    try {
      const response = await authClient.post<RecoverPasswordResponse>(
        `${this.getAuthEndpoint()}/recover-password`,
        input
      );
      return response.data;
    } catch (error) {
      console.error('Recover password error:', error);
      return {
        error: true,
        message:
          errorBody<RecoverPasswordResponse>(error)?.message || 'network_error',
      };
    }
  }

  /**
   * Validate a password reset token
   */
  async validateResetToken(hash: string): Promise<ValidateResetTokenResponse> {
    try {
      const response = await authClient.post<ValidateResetTokenResponse>(
        `${this.getAuthEndpoint()}/validate-reset-token`,
        { hash }
      );
      return response.data;
    } catch (error) {
      console.error('Validate reset token error:', error);
      return {
        error: true,
        message:
          errorBody<ValidateResetTokenResponse>(error)?.message ||
          'invalid_token',
      };
    }
  }

  /**
   * Reset password using a valid token
   */
  async resetPassword(
    hash: string,
    password: string
  ): Promise<ResetPasswordResponse> {
    try {
      const response = await authClient.post<ResetPasswordResponse>(
        `${this.getAuthEndpoint()}/reset-password`,
        { hash, password }
      );
      return response.data;
    } catch (error) {
      console.error('Reset password error:', error);
      return {
        error: true,
        message:
          errorBody<ResetPasswordResponse>(error)?.message || 'network_error',
      };
    }
  }

  /**
   * Get CSRF token for requests
   */
  getCsrfToken(): string {
    // Try memory first
    if (this.csrfToken) {
      return this.csrfToken;
    }

    // Try localStorage (doesn't exist in React Native)
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const stored = localStorage.getItem(CSRF_STORAGE_KEY);
      if (stored) {
        this.csrfToken = stored;
        return stored;
      }
    }

    // Try to read from cookie (non-HttpOnly cookie) - not available in React Native
    if (typeof document !== 'undefined' && typeof document.cookie === 'string') {
      const cookies = document.cookie.split(';');
      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === CSRF_COOKIE_NAME && value) {
          this.csrfToken = value;
          this.storeCsrfToken(value);
          return value;
        }
      }
    }

    return '';
  }

  /**
   * Set CSRF token (called after login/refresh)
   */
  private setCsrfToken(token: string): void {
    this.csrfToken = token;
    this.storeCsrfToken(token);
  }

  /**
   * Store CSRF token in localStorage
   */
  private storeCsrfToken(token: string): void {
    // localStorage doesn't exist in React Native
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      localStorage.setItem(CSRF_STORAGE_KEY, token);
    }
  }

  /**
   * Clear CSRF token from memory, storage, and cookie
   */
  clearCsrfToken(): void {
    this.csrfToken = null;
    // localStorage doesn't exist in React Native
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      localStorage.removeItem(CSRF_STORAGE_KEY);
    }
    // Also clear the cookie (set expired date)
    if (typeof document !== 'undefined') {
      document.cookie = `${CSRF_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    }
  }
}

// Export singleton instance
export const authService = new AuthService();

// Export class for testing
export { AuthService };
